import { useMemo, useState } from 'react';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Link, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { useSupabaseQuery } from '@/hooks/useSupabaseQuery';
import { format } from 'date-fns';
import type { PlanStatus } from '@shared/index';
import { Loader2, MoreHorizontal } from 'lucide-react';
import PlanStatusBadge from '@/pages/plans/components/PlanStatusBadge';
import PlanFiltersBar from '@/pages/plans/components/PlanFiltersBar';
import PlansBulkActions from '@/pages/plans/components/PlansBulkActions';

export interface PlanTableItem {
  id: string;
  name: string;
  slug: string;
  status: PlanStatus;
  brand: string | null;
  category: string | null;
  updatedAt: string;
  publishedAt: string | null;
  revision: number;
  publishedRevision: number | null;
}

type PlanRow = {
  id: string;
  name: string;
  slug: string;
  status: string;
  rev: number;
  published_rev: number | null;
  published_at: string | null;
  updated_at: string;
  plan_groups?:
    | { brand: string | null; category: string | null }
    | Array<{ brand: string | null; category: string | null }>
    | null;
};

const PlansListPage = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState({
    brand: 'all',
    category: 'all',
    status: 'all',
    search: '',
  });

  const queryKey = ['plans', filters] as const;
  const plansQuery = useSupabaseQuery<PlanTableItem[]>(queryKey, async () => {
    const client = supabase;
    if (!client) return [];

    let query = client
      .from('plans')
      .select('id, name, slug, status, rev, published_rev, published_at, updated_at, plan_groups(brand, category)')
      .order('updated_at', { ascending: false });

    if (filters.status !== 'all') {
      query = query.eq('status', filters.status);
    }

    if (filters.brand !== 'all') {
      query = query.eq('plan_groups.brand', filters.brand);
    }

    if (filters.category !== 'all') {
      query = query.eq('plan_groups.category', filters.category);
    }

    if (filters.search) {
      query = query.ilike('name', `%${filters.search}%`);
    }

    const { data, error } = await query;

    if (error) {
      throw error;
    }

    return ((data ?? []) as PlanRow[]).map((row) => {
      const planGroup = Array.isArray(row.plan_groups) ? row.plan_groups[0] : row.plan_groups;
      return {
        id: row.id,
        name: row.name,
        slug: row.slug,
        status: row.status as PlanStatus,
        brand: planGroup?.brand ?? null,
        category: planGroup?.category ?? null,
        updatedAt: row.updated_at,
        publishedAt: row.published_at,
        revision: row.rev,
        publishedRevision: row.published_rev,
      } satisfies PlanTableItem;
    });
  });

  const columns = useMemo<ColumnDef<PlanTableItem>[]>(
    () => [
      {
        header: 'Plan',
        accessorKey: 'name',
        cell: ({ row }) => (
          <div className="flex flex-col">
            <Link to={`/plans/${row.original.id}`} className="font-medium text-slate-100 hover:underline">
              {row.original.name}
            </Link>
            <span className="text-xs text-slate-400">Slug: {row.original.slug}</span>
          </div>
        ),
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: ({ row }) => <PlanStatusBadge status={row.original.status} />,
      },
      {
        header: 'Brand',
        accessorKey: 'brand',
        cell: ({ getValue }) => <span className="text-sm text-slate-200">{getValue<string | null>() ?? '—'}</span>,
      },
      {
        header: 'Category',
        accessorKey: 'category',
        cell: ({ getValue }) => <span className="text-sm capitalize text-slate-200">{getValue<string | null>() ?? '—'}</span>,
      },
      {
        header: 'Updated',
        accessorKey: 'updatedAt',
        cell: ({ getValue }) => {
          const value = getValue<string>();
          return <span className="text-sm text-slate-300">{format(new Date(value), 'MMM d, yyyy p')}</span>;
        },
      },
      {
        header: 'Revision',
        accessorKey: 'revision',
        cell: ({ row }) => (
          <div className="flex flex-col text-xs text-slate-400">
            <span>Draft: r{row.original.revision}</span>
            <span>Published: {row.original.publishedRevision ? `r${row.original.publishedRevision}` : '—'}</span>
          </div>
        ),
      },
      {
        id: 'actions',
        cell: ({ row }) => (
          <PlansRowActions plan={row.original} />
        ),
      },
    ],
    []
  );

  const table = useReactTable({
    data: plansQuery.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const handleCreatePlan = () => {
    navigate('/plans/new');
  };

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-100">Plans Manager</h1>
          <p className="text-sm text-slate-400">Manage plan catalog, pricing, terms, and publish state.</p>
        </div>
        <button
          type="button"
          onClick={handleCreatePlan}
          className="rounded-md bg-emerald-500 px-4 py-2 text-sm font-medium text-white shadow hover:bg-emerald-600"
        >
          Create Plan
        </button>
      </header>

      <PlanFiltersBar filters={filters} onChange={setFilters} />

      <div className="rounded-lg border border-slate-800 bg-slate-900/60">
        {plansQuery.isFetching ? (
          <div className="flex h-48 items-center justify-center text-slate-400">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading plans
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-800">
              <thead className="bg-slate-900/80">
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th key={header.id} className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-400">
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-slate-800">
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-800/40">
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-4 py-3 text-sm text-slate-200">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
                {!plansQuery.data?.length && (
                  <tr>
                    <td colSpan={columns.length} className="px-4 py-16 text-center text-sm text-slate-400">
                      No plans match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <PlansBulkActions
        selectedPlans={[]}
        onAfterAction={() => queryClient.invalidateQueries({ queryKey: ['plans'] })}
      />
    </div>
  );
};

interface PlansRowActionsProps {
  plan: PlanTableItem;
}

const PlansRowActions = ({ plan }: PlansRowActionsProps) => {
  const [isBusy, setIsBusy] = useState(false);
  const queryClient = useQueryClient();

  const handlePublish = async () => {
    const client = supabase;
    if (!client) return;
    setIsBusy(true);
    try {
      const { error } = await client.functions.invoke('plan-publish', { body: { plan_id: plan.id } });
      if (error) throw error;
      toast.success(`Plan “${plan.name}” published`);
      queryClient.invalidateQueries({ queryKey: ['plans'] });
    } catch (error) {
      toast.error('Failed to publish plan', {
        description: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsBusy(false);
    }
  };

  const handleSuspend = async () => {
    const client = supabase;
    if (!client) return;
    setIsBusy(true);
    try {
      const { error } = await client.functions.invoke('plan-suspend', {
        body: { plan_id: plan.id, reason: 'Admin suspend from Plans grid' },
      });
      if (error) throw error;
      toast.success(`Plan “${plan.name}” suspended`);
      queryClient.invalidateQueries({ queryKey: ['plans'] });
    } catch (error) {
      toast.error('Failed to suspend plan', {
        description: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-2 text-sm">
      <Link to={`/plans/${plan.id}`} className="rounded-md border border-slate-700 px-2 py-1 text-slate-200 hover:bg-slate-800/60">
        Edit
      </Link>
      <button
        type="button"
        onClick={handlePublish}
        disabled={isBusy}
        className="rounded-md border border-emerald-500 px-2 py-1 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50"
      >
        Publish
      </button>
      <button
        type="button"
        onClick={handleSuspend}
        disabled={isBusy}
        className="rounded-md border border-amber-500 px-2 py-1 text-amber-400 hover:bg-amber-500/10 disabled:opacity-50"
      >
        Suspend
      </button>
      <button type="button" className="rounded-md border border-slate-700 p-2 text-slate-300 hover:bg-slate-800/60">
        <MoreHorizontal size={16} />
      </button>
    </div>
  );
};

export default PlansListPage;
